import React, { useEffect, useMemo, useRef, useState } from "react";
import { Spinner, UncontrolledTooltip } from "reactstrap";

import useFetch from "Hooks/useFetch";
import styles from "Pages/Run/Components/ActivityHistogram/ActivityHistogram.module.css";

interface IActivityMonth {
    year: number;
    month: number;
    runs: number;
}

interface IActivity {
    months: IActivityMonth[];
    first_run: string | null;
    last_run: string | null;
    total_runs: number;
}

interface IActivityDay {
    day: number;
    runs: number;
}

/** The table only ever loads this many rows, so a month above it cannot be browsed from the
 *  table — it gets zoomed to days instead. Mirrors max_rows in MessageHeaders.cs. */
const TABLE_ROW_CAP = 500;

/** A single day that is ALREADY OVER is fetched whole up to this many rows instead of the 500
 *  cap (GetMaxRows, MessageHeaders.cs:544). Today never gets the lift -- it polls every 3 s and
 *  gains rows -- so a selected TODAY is capped like any other range. */
const SINGLE_PAST_DAY_CAP = 10000;

interface IProps {
    /** Called with a from/to pair (yyyy-mm-dd) when a bar is clicked. */
    onPickRange: (range: { from: string; to: string }) => void;
    /** The From/Through currently in force on the page. The highlighted bar is DERIVED from
     *  this, so a range typed into the picker lights the same bar a click would have, and Reset
     *  (empty range) clears the highlight and closes the day strip. */
    selectedRange: { from: string; to: string };
}

const MONTH_NAMES = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function pad(n: number): string {
    return n < 10 ? `0${n}` : `${n}`;
}

function lastDayOfMonth(year: number, month: number): number {
    return new Date(year, month, 0).getDate();
}

/** Local-day comparison, matching isCurrentRangeASinglePastDay in Run.tsx: only a day that is
 *  already over gets the single-past-day lift. */
function isPastDay(year: number, month: number, day: number): boolean {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    return new Date(year, month - 1, day) < startOfToday;
}

/**
 * Runs per month across the company's whole history. The FIRST NON-ZERO BAR is the answer to
 * "when did this client first run?" — the question the row list below cannot answer, because it
 * only ever loads the newest 500 rows (dimension-nlgpu). Clicking a bar filters the table to that
 * month.
 *
 * Fetched ONCE, not on the run page's 3-second poll: it is an aggregate over full history, and
 * history older than today does not change between polls.
 */
function parseYmd(value: string): { y: number; m: number; d: number } | undefined {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) return undefined;
    return { y: Number(match[1]), m: Number(match[2]), d: Number(match[3]) };
}

export default function ActivityHistogram({ onPickRange, selectedRange }: IProps) {
    const [activity, setActivity] = useState<IActivity | undefined>();
    /** Brush in progress: mouse went down on bar `start` and is currently over bar `end` (indexes
     *  into `bars` or into the day strip). Released on window mouseup. */
    const [drag, setDrag] = useState<
        { kind: "month" | "day"; start: number; end: number } | undefined
    >();
    const dragRef = useRef(drag);
    dragRef.current = drag;
    const [isLoading, setIsLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [zoom, setZoom] = useState<
        | {
              year: number;
              month: number;
              days: IActivityDay[] | undefined;
              failed: boolean;
              /** The range has been seen pointing at this month at least once. Until then an
               *  empty range is the PRE-click state still in flight, not a reset. */
              armed: boolean;
          }
        | undefined
    >();
    const { get } = useFetch();

    /** Which month bar (and which day, if the range is a single day) the current range means. */
    const selection = useMemo(() => {
        const from = parseYmd(selectedRange.from);
        const to = parseYmd(selectedRange.to);
        if (!from || !to) {
            return { monthKey: "", day: undefined as number | undefined, from, to };
        }
        if (from.y === to.y && from.m === to.m && from.d === to.d) {
            return { monthKey: `${from.y}-${from.m}`, day: from.d, from, to };
        }
        const isWholeMonth =
            from.y === to.y &&
            from.m === to.m &&
            from.d === 1 &&
            to.d === lastDayOfMonth(to.y, to.m);
        return { monthKey: isWholeMonth ? `${from.y}-${from.m}` : "", day: undefined, from, to };
    }, [selectedRange.from, selectedRange.to]);

    /** Does the picker's range (any shape, incl. the default 3-year window) touch this month? Lit
     *  in a lighter tint than the exact selection so picker and strip always agree visually. */
    function isMonthInRange(bar: IActivityMonth): boolean {
        const { from, to } = selection;
        if (!from || !to) return false;
        const key = bar.year * 12 + bar.month;
        return key >= from.y * 12 + from.m && key <= to.y * 12 + to.m;
    }

    function isDayInRange(year: number, month: number, day: number): boolean {
        const { from, to } = selection;
        if (!from || !to) return false;
        const key = `${year}-${pad(month)}-${pad(day)}`;
        return key >= selectedRange.from && key <= selectedRange.to;
    }

    /** Is bar `index` inside the brush being dragged right now (preview highlight)? */
    function isInDrag(kind: "month" | "day", index: number): boolean {
        if (!drag || drag.kind !== kind) return false;
        return index >= Math.min(drag.start, drag.end) && index <= Math.max(drag.start, drag.end);
    }

    function beginDrag(e: React.MouseEvent, kind: "month" | "day", index: number) {
        if (e.button !== 0) return;
        e.preventDefault(); // no text selection while brushing
        setDrag({ kind, start: index, end: index });
    }

    function extendDrag(kind: "month" | "day", index: number) {
        setDrag((d) => (d && d.kind === kind && d.end !== index ? { ...d, end: index } : d));
    }

    // Release anywhere ends the brush. One bar = the existing single click; a span = a range.
    useEffect(() => {
        function onUp() {
            const d = dragRef.current;
            if (!d) return;
            setDrag(undefined);
            const lo = Math.min(d.start, d.end);
            const hi = Math.max(d.start, d.end);
            if (d.kind === "month") {
                const first = barsRef.current[lo];
                const last = barsRef.current[hi];
                if (!first || !last) return;
                if (lo === hi) {
                    pickMonth(first);
                    return;
                }
                setZoom(undefined);
                onPickRange({
                    from: `${first.year}-${pad(first.month)}-01`,
                    to: `${last.year}-${pad(last.month)}-${pad(lastDayOfMonth(last.year, last.month))}`,
                });
            } else {
                const z = zoomRef.current;
                if (!z) return;
                onPickRange({
                    from: `${z.year}-${pad(z.month)}-${pad(lo + 1)}`,
                    to: `${z.year}-${pad(z.month)}-${pad(hi + 1)}`,
                });
            }
        }
        window.addEventListener("mouseup", onUp);
        return () => window.removeEventListener("mouseup", onUp);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onPickRange]);
    const selected = selection.monthKey;
    const selectedDay = selection.day;

    // Reset (empty range) or a DIFFERENT whole month closes the day strip. Right after a bar click
    // the range has not yet round-tripped through the picker: it still holds the PREVIOUS value —
    // empty on a fresh page — so neither a non-matching monthKey nor an empty range may close the
    // strip until the range has been seen on the zoomed month once (`armed`). Closing on that
    // instant killed the strip on the first click of every page load.
    useEffect(() => {
        if (!zoom) return;
        const zoomKey = `${zoom.year}-${zoom.month}`;
        if (!zoom.armed) {
            if (selection.monthKey === zoomKey) setZoom({ ...zoom, armed: true });
            return;
        }
        const isReset = selectedRange.from === "" && selectedRange.to === "";
        const isOtherMonth = selection.monthKey !== "" && selection.monthKey !== zoomKey;
        if (isReset || isOtherMonth) {
            setZoom(undefined);
        }
    }, [selection.monthKey, selectedRange.from, selectedRange.to, zoom]);

    useEffect(() => {
        let ignore = false;

        getActivity();

        // Seconds, not milliseconds, on a large company (~4s for MATT's 6.18M rows). Nothing
        // waits on it: the panel is already open and usable, and the strip fills in when it
        // arrives. It is never fetched on page load — the component does not mount until the
        // search panel is opened.
        async function getActivity() {
            try {
                const response = await get("api/run/activity");
                if (ignore) return;
                if (!response.ok) {
                    setFailed(true);
                    return;
                }
                setActivity(await response.json());
            } catch {
                if (!ignore) setFailed(true);
            } finally {
                if (!ignore) setIsLoading(false);
            }
        }

        return () => {
            ignore = true;
        };
    }, [get]);

    const zoomRef = useRef(zoom);
    zoomRef.current = zoom;

    /** Every month from the first run to the last, so gaps render as gaps rather than closing up. */
    const bars = useMemo(() => {
        if (!activity || activity.months.length === 0) return [];

        const counts = new Map(
            activity.months.map((m) => [`${m.year}-${m.month}`, m.runs])
        );

        const first = activity.months[0];
        const last = activity.months[activity.months.length - 1];

        const all: IActivityMonth[] = [];
        for (let y = first.year; y <= last.year; y++) {
            const from = y === first.year ? first.month : 1;
            const to = y === last.year ? last.month : 12;
            for (let m = from; m <= to; m++) {
                all.push({ year: y, month: m, runs: counts.get(`${y}-${m}`) ?? 0 });
            }
        }
        return all;
    }, [activity]);
    const barsRef = useRef(bars);
    barsRef.current = bars;

    const max = useMemo(
        () => bars.reduce((acc, b) => Math.max(acc, b.runs), 0),
        [bars]
    );

    if (isLoading) {
        return (
            <div className={styles.wrap}>
                <div className={styles.header}>
                    <Spinner size="sm" color="secondary" />
                    <span className={styles.total}>
                        Loading run history — seconds on a large company…
                    </span>
                </div>
                <div className={styles.chartPlaceholder} />
            </div>
        );
    }

    if (failed) {
        return (
            <div className={styles.wrap}>
                <div className={styles.header}>
                    <span className={styles.total}>
                        Run history unavailable.
                    </span>
                </div>
            </div>
        );
    }

    if (!activity || bars.length === 0) return null;

    const firstRunLabel = activity.first_run
        ? `${MONTH_NAMES[new Date(activity.first_run).getMonth()]} ${new Date(
              activity.first_run
          ).getFullYear()}`
        : "—";

    function pickMonth(bar: IActivityMonth) {
        onPickRange({
            from: `${bar.year}-${pad(bar.month)}-01`,
            to: `${bar.year}-${pad(bar.month)}-${pad(
                lastDayOfMonth(bar.year, bar.month)
            )}`,
        });

        // A month the table can show in full needs no second level. One above the cap does:
        // "newest 500 of 51,588" is not browsing, so drill to days (Splunk/GCP re-bucket the same
        // way). The day strip is its own async fetch with its own loading state.
        if (bar.runs <= TABLE_ROW_CAP) {
            setZoom(undefined);
            return;
        }
        setZoom({ year: bar.year, month: bar.month, days: undefined, failed: false, armed: false });
        loadDays(bar.year, bar.month);
    }

    async function loadDays(year: number, month: number) {
        try {
            const response = await get(`api/run/activity/${year}/${month}`);
            if (!response.ok) {
                setZoom((z) => (z && z.year === year && z.month === month ? { ...z, failed: true } : z));
                return;
            }
            const data = await response.json();
            setZoom((z) =>
                z && z.year === year && z.month === month ? { ...z, days: data.days } : z
            );
        } catch {
            setZoom((z) => (z && z.year === year && z.month === month ? { ...z, failed: true } : z));
        }
    }


    return (
        <div className={styles.wrap}>
            <div className={styles.header}>
                <span>
                    <strong>First run:</strong> {firstRunLabel}
                </span>
                <span className={styles.total}>
                    {activity.total_runs.toLocaleString()} runs all time
                </span>
                <span className={styles.hint}>1 bar = 1 month · click to filter · drag for a range</span>
            </div>

            <div className={styles.chart}>
                {bars.map((bar, index) => {
                    const key = `${bar.year}-${bar.month}`;
                    const id = `bar-${key}`;
                    // Square root, not linear: one busy month otherwise flattens every other bar
                    // to nothing and the early history — the part being looked for — disappears.
                    const height =
                        bar.runs === 0
                            ? 0
                            : Math.max(
                                  2,
                                  Math.round(
                                      (Math.sqrt(bar.runs) / Math.sqrt(max)) * 46
                                  )
                              );
                    return (
                        <div
                            key={key}
                            id={id}
                            className={`${styles.barSlot} ${
                                selected === key
                                    ? styles.barSelected
                                    : isInDrag("month", index) || isMonthInRange(bar)
                                    ? styles.barInRange
                                    : ""
                            }`}
                            onMouseDown={(e) => beginDrag(e, "month", index)}
                            onMouseEnter={() => extendDrag("month", index)}
                        >
                            <div
                                className={
                                    bar.runs === 0 ? styles.barEmpty : styles.bar
                                }
                                style={{ height: `${height}px` }}
                            />
                            <UncontrolledTooltip target={id}>
                                {`${MONTH_NAMES[bar.month - 1]} ${bar.year} — ${
                                    bar.runs
                                } run${bar.runs === 1 ? "" : "s"}`}
                            </UncontrolledTooltip>
                        </div>
                    );
                })}
            </div>

            <div className={styles.axis}>
                {bars.map((bar) => (
                    <div key={`${bar.year}-${bar.month}`} className={styles.tick}>
                        {bar.month === 1 || bar === bars[0] ? (
                            <span className={styles.tickLabel}>{bar.year}</span>
                        ) : null}
                    </div>
                ))}
            </div>

            {zoom && renderDayStrip(zoom)}
        </div>
    );

    /**
     * THE NOTICE MUST DESCRIBE THE RANGE ACTUALLY IN FORCE, not the month whose day strip is
     * open. Those two diverge the instant a day is clicked: the strip stays open on the month
     * while the table switches to one day, which is fetched WHOLE up to 10,000 rows. The
     * sentence used to be unconditional, so prod showed "Sep 2026 — more than 500 runs, so the
     * table shows only the newest 500" directly above a grid holding all 607 rows of Sep 2
     * (dimension-cjg3p).
     *
     * It also states the CAP as a rule rather than asserting this table is truncated. A bar
     * counts every run including Integration Manager, which the table's own query excludes
     * (MessageHeaders.cs:321-325), so a bar above 500 does not prove the table filled its quota.
     * Whether it actually did is reported by the server's truncated flag — the note at
     * Run.tsx:631 and the "+" on the row footer — which is the authoritative answer.
     */
    function renderZoomNotice(z: NonNullable<typeof zoom>) {
        const monthTitle = `${MONTH_NAMES[z.month - 1]} ${z.year}`;
        const monthKey = `${z.year}-${z.month}`;

        if (selection.day !== undefined && selection.monthKey === monthKey) {
            const dayTitle = `${MONTH_NAMES[z.month - 1]} ${selection.day}, ${z.year}`;

            // Today keeps the 500 cap: it polls every 3 s and gains rows, so it is never fetched
            // whole (GetMaxRows, MessageHeaders.cs:544).
            if (!isPastDay(z.year, z.month, selection.day)) {
                return (
                    <>
                        <strong>{dayTitle}</strong> — today, so the table loads the newest{" "}
                        {TABLE_ROW_CAP} runs and keeps refreshing as more run.
                    </>
                );
            }

            return (
                <>
                    <strong>{dayTitle}</strong> — the table loads this day whole, up to{" "}
                    {SINGLE_PAST_DAY_CAP.toLocaleString()} runs.
                </>
            );
        }

        // The whole month, a multi-day brush inside it, or a range that reaches outside it: every
        // one of those is capped at 500, because both lifts are single-day only.
        return (
            <>
                <strong>{monthTitle}</strong> — the table loads at most the newest{" "}
                {TABLE_ROW_CAP} runs of a range this wide. Click a day to load it whole (up to{" "}
                {SINGLE_PAST_DAY_CAP.toLocaleString()}).
            </>
        );
    }

    function renderDayStrip(z: NonNullable<typeof zoom>) {
        const title = `${MONTH_NAMES[z.month - 1]} ${z.year}`;

        if (z.failed) {
            return (
                <div className={styles.zoom}>
                    <span className={styles.total}>Day breakdown for {title} unavailable.</span>
                </div>
            );
        }

        if (!z.days) {
            return (
                <div className={styles.zoom}>
                    <div className={styles.header}>
                        <Spinner size="sm" color="secondary" />
                        <span className={styles.total}>Loading days in {title}…</span>
                    </div>
                    <div className={styles.dayPlaceholder} />
                </div>
            );
        }

        const counts = new Map(z.days.map((d) => [d.day, d.runs]));
        const dayCount = lastDayOfMonth(z.year, z.month);
        const dayMax = z.days.reduce((acc, d) => Math.max(acc, d.runs), 0);
        const days = Array.from({ length: dayCount }, (_, i) => ({
            day: i + 1,
            runs: counts.get(i + 1) ?? 0,
        }));

        return (
            <div className={styles.zoom}>
                <div className={styles.header}>
                    <span>{renderZoomNotice(z)}</span>
                    <span className={styles.hint}>1 bar = 1 day · drag for a range</span>
                    <button
                        type="button"
                        className={styles.zoomClose}
                        onClick={() => setZoom(undefined)}
                    >
                        close
                    </button>
                </div>
                <div className={styles.chart}>
                    {days.map((d) => {
                        const id = `day-${z.year}-${z.month}-${d.day}`;
                        const height =
                            d.runs === 0
                                ? 0
                                : Math.max(2, Math.round((Math.sqrt(d.runs) / Math.sqrt(dayMax)) * 46));
                        return (
                            <div
                                key={id}
                                id={id}
                                className={`${styles.barSlot} ${
                                    selectedDay === d.day
                                        ? styles.barSelected
                                        : isInDrag("day", d.day - 1) ||
                                          isDayInRange(z.year, z.month, d.day)
                                        ? styles.barInRange
                                        : ""
                                }`}
                                onMouseDown={(e) => beginDrag(e, "day", d.day - 1)}
                                onMouseEnter={() => extendDrag("day", d.day - 1)}
                            >
                                <div
                                    className={d.runs === 0 ? styles.barEmpty : styles.bar}
                                    style={{ height: `${height}px` }}
                                />
                                <UncontrolledTooltip target={id}>
                                    {`${MONTH_NAMES[z.month - 1]} ${d.day} — ${d.runs} run${
                                        d.runs === 1 ? "" : "s"
                                    }`}
                                </UncontrolledTooltip>
                            </div>
                        );
                    })}
                </div>
                <div className={styles.axis}>
                    {days.map((d) => (
                        <div key={d.day} className={styles.tick}>
                            {d.day === 1 || d.day % 5 === 0 ? (
                                <span className={styles.tickLabel}>{d.day}</span>
                            ) : null}
                        </div>
                    ))}
                </div>
            </div>
        );
    }
}
